<template>
  <div class="tw-h-full tw-flex">
    <div class="tw-flex tw-flex-1 tw-flex-col">
      <div
        class="tw-py-4 tw-h-full tw-overflow-y-auto"
        data-testid="meeting-preparation-remarks"
      >
        <div>
          <div class="tw-flex tw-flex-col tw-gap-1 tw-pt-10">
            <div class="tw-text-[24px] tw-font-medium tw-mb-8">
              {{ copy.titleWithDate }}
            </div>
            <div
              v-if="pinnedReportsVisible"
              class="tw-mb-6"
              data-testid="present-pinned-reports"
            >
              <PinnedReportCardList
                :reports="pinnedReports"
                :loading="pinnedReportsLoading"
                :errored="pinnedReportsErrored"
                :expected-count="pageData.pinnedReportsCount"
                @retry="fetchPinnedReports"
              />
            </div>
            <LinksSection
              :links="pageData.links"
              :reports="pageData.reports"
              :editable="false"
              display-title
            />
            <TalkingPoints
              :items="orderedItems"
              :presentation-comments="presentationComments"
              :loading="loading"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount, inject } from 'vue'
import { useStore } from 'vuex'
import { orderBy } from 'lodash'
import type { PresentPageData } from '@app/application/client_meetings/types'
import LinksSection from '@app/application/client_meetings/screens/prepare/components/links-section.vue'
import PinnedReportCardList from '@app/application/report/components/pinned-report-card-list.vue'
import TalkingPoints from '@app/application/client_meetings/screens/present/components/talking-points.vue'

import useOrderedRemarks from '@app/application/client_meetings/screens/prepare/composables/use-ordered-remarks'
import usePresentationComments from '@app/application/report/composables/use-presentation-comments'
import {
  presentModePinnedCardsUrl,
  usePinnedReportCards,
} from '@app/application/report/composables/use-pinned-report-cards'

import { SfTime } from '@app/adapters/time'
import useI18n from '@app/shared/vue/composables/use-I18n'

const pageData = inject('pageData') as PresentPageData
const store = useStore()

const I18n = useI18n()
const copy = {
  titleWithDate: I18n.t('client_meetings.present.meeting_date', {
    date: SfTime(new Date()).format('DD/MM/YYYY'),
  }),
}

const { presentationComments } = usePresentationComments()

const {
  reports: pinnedReports,
  loading: pinnedReportsLoading,
  errored: pinnedReportsErrored,
  fetchReports: fetchPinnedReports,
} = usePinnedReportCards(presentModePinnedCardsUrl)

const pinnedReportsActive =
  pageData.pinnedReportsEnabled && pageData.pinnedReportsCount > 0

const pinnedReportsVisible = computed(
  () =>
    pinnedReportsActive &&
    (pinnedReportsLoading.value ||
      pinnedReportsErrored.value ||
      pinnedReports.value.length > 0)
)

if (pinnedReportsActive) {
  void fetchPinnedReports()
}

const remarks = computed(() => {
  return orderBy(
    Object.values(store.state.remarks.remarks),
    'sortTimestamp',
    'desc'
  )
})

store.dispatch('setInitialOrder', pageData.initialOrder)

const { orderedItems, joinUnordered } = useOrderedRemarks()

const loading = computed(
  () =>
    remarks.value.length === 0 && store.getters.fetchingCommunicationsInProgress
)

async function refreshCommunicationData(
  data: { action?: string; remark?: { id: string } } = {}
) {
  await store.dispatch('fetchCommunication')

  if (data.action === 'createRemark') {
    window.eventHub.$emit('highlightRemark', {
      elementId: `remark-${data.remark?.id}`,
      force: true,
    })
  }

  if (Object.keys(data).length !== 0) {
    joinUnordered()
  }
}
store.dispatch('fetchMentionUsernames')

onMounted(() => {
  window.eventHub.$on('refreshCommunicationData', refreshCommunicationData)
})

onBeforeUnmount(() => {
  window.eventHub.$off('refreshCommunicationData', refreshCommunicationData)
})

refreshCommunicationData()
</script>
